/**
 * Entlohnungsarten (AP-22 Teil 3).
 *
 * Wie die Mitarbeiter eines Betriebs entlohnt werden: nach einem
 * Kollektivvertrag (nach Stunden) oder pauschal. Ein Betrieb mit genau einem
 * Kollektivvertrag braucht hier nichts — wer keine Entlohnungsart zugeordnet
 * hat, rechnet mit den Einstellungen „für alle".
 *
 * Zugeordnet wird am Arbeitszeitmodell des Mitarbeiters. Eigene Schwellen und
 * Sätze je Entlohnungsart stehen unter Einstellungen, eigene Lohnarten unter
 * BMD-Export.
 *
 * AP-36: Zeilenblock. Jede Zeile ist eine Speicherstelle
 * (`payrollCompensation:<id>`), die leere letzte Zeile legt an.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { erfuelltRecht } from '@symphenia/shared-types';
import { api } from '@/lib/axios';
import { useAuthStore } from '@/lib/auth.store';
import { apiErrorText } from '@/lib/api-error';
import { pageWide } from '@/lib/form-styles';
import { ZeilenBlock, ZeilenListe, Feld as Spalte, zelle, type Spalte as SpaltenDef } from '@/components/zeilenblock';
import {
  FeldStatusZeichen,
  mitStand,
  useFeld,
  useGruppe,
  useSpeicherstelle,
  useStellenDaten,
  type Datensatz,
  type Speicherstelle,
} from '@/lib/speichern';
import { KV_VORLAGEN } from '@symphenia/payroll-core';
import { createCollectiveAgreement, fetchCollectiveAgreements } from '../api/payroll.api';
import type { CollectiveAgreement } from '../types/payroll.types';

const SPALTEN: SpaltenDef[] = [
  { key: 'name', label: 'Bezeichnung', spur: 'minmax(8rem,3fr)', min: 130, schmal: 'w-full' },
  { key: 'kind', label: 'Art', spur: '9.5rem', min: 120, schmal: 'w-40' },
  { key: 'flatMode', label: 'Stunden', spur: '11.5rem', min: 140, schmal: 'w-48' },
  { key: 'includedOvertimeHours', label: 'abgegolten h', spur: '6.5rem', min: 80, schmal: 'w-28', rechts: true },
  { key: 'templateKey', label: 'KV-Vorlage', spur: '11rem', min: 140, schmal: 'w-48' },
  { key: 'employees', label: 'MA', spur: '3.5rem', min: 40, schmal: 'w-20', rechts: true },
  { key: 'isActive', label: 'Aktiv', spur: '3.5rem', min: 44, schmal: 'w-16' },
];

export const MODUS_TEXT: Record<string, string> = {
  ABSENCES_ONLY: 'nur Fehlzeiten',
  TRACK_ONLY: 'zur Kontrolle',
  OVERTIME_INCLUDED: 'Überstundenpauschale',
};

export default function PayrollCompensationPage() {
  const rechte = useAuthStore((s) => s.user?.permissions);
  const darfPflegen = erfuelltRecht('payroll.collective_agreement.manage', rechte);
  const [rows, setRows] = useState<CollectiveAgreement[]>([]);
  const [geladen, setGeladen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const laden = useCallback(async () => {
    setError(null);
    try {
      setRows(await fetchCollectiveAgreements());
    } catch (err) {
      setError(apiErrorText(err, 'Die Entlohnungsarten konnten nicht geladen werden.'));
    } finally {
      setGeladen(true);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  return (
    <div className={pageWide}>
      <div>
        <h1 className="text-3xl font-bold text-foreground">Entlohnungsarten</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Wie Mitarbeiter entlohnt werden: nach einem Kollektivvertrag oder pauschal. Gilt für alle derselbe
          Kollektivvertrag, brauchst du hier nichts anzulegen. Eine Zeile wird gespeichert, sobald du sie verlässt.
        </p>
      </div>

      {error && <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}

      <ZeilenListe spalten={SPALTEN}>
        {!geladen && <p className="px-4 py-6 text-center text-sm text-muted-foreground">Laden…</p>}
        {geladen && rows.length === 0 && (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            Noch keine Entlohnungsart — alle Mitarbeiter rechnen mit den Einstellungen „für alle".
            {darfPflegen && ' Leg in der Zeile unten die erste an, z. B. „Büro pauschal".'}
          </p>
        )}
        {rows.map((e) => (
          <ArtZeile key={e.id} art={e} gesperrt={!darfPflegen} />
        ))}
        {darfPflegen && <NeueArt onAngelegt={() => void laden()} onFehler={setError} />}
      </ZeilenListe>

      <div className="space-y-2 text-xs text-muted-foreground">
        <p>
          <span className="font-medium text-foreground">Zuordnen:</span> im{' '}
          <Link to="/payroll/work-schedules" className="text-primary hover:underline">
            Arbeitszeitmodell
          </Link>{' '}
          des Mitarbeiters, mit Gültig-ab. Ein Wechsel der Entlohnungsart gilt ab einem Monatsersten.
        </p>
        <p>
          <span className="font-medium text-foreground">Kollektivvertrag:</span> gerechnet wird nach Stunden. Die{' '}
          <span className="font-medium text-foreground">KV-Vorlage</span> bringt den Standard des Vertrags mit — ohne Wahl gilt
          die des Betriebs. Eigene Schwellen, Sätze und Zulagen trägst du unter{' '}
          <Link to="/payroll/settings" className="text-primary hover:underline">
            Einstellungen
          </Link>{' '}
          für diese Entlohnungsart ein; was dort fehlt, gilt wie „für alle".
        </p>
        <ul className="space-y-1">
          <li>
            <span className="font-medium text-foreground">Pauschal, nur Fehlzeiten:</span> Es werden keine Stunden erfasst — nur
            Urlaub, Krankenstand und andere Fehlzeiten. Ein Monat ohne Tageszeiten ist vollständig; Mehrarbeit, Überstunden und
            Zeitausgleich gibt es nicht.
          </li>
          <li>
            <span className="font-medium text-foreground">Pauschal, zur Kontrolle:</span> Stunden werden erfasst und gerechnet,
            sind aber mit der Pauschale abgegolten. Mehrarbeit, Überstunden und Zeitausgleich gehen nicht nach BMD.
          </li>
          <li>
            <span className="font-medium text-foreground">Überstundenpauschale:</span> Die eingetragenen Stunden je Monat sind
            abgegolten — zuerst Mehrarbeit, dann Überstunden. Nur was darüber liegt, wird ausgewiesen und übergeben.
          </li>
        </ul>
        <p>
          Eigene BMD-Lohnarten je Entlohnungsart stellst du im{' '}
          <Link to="/payroll/bmd" className="text-primary hover:underline">
            BMD-Export
          </Link>{' '}
          ein.
        </p>
      </div>
    </div>
  );
}

function ArtZeile({ art, gesperrt }: { art: CollectiveAgreement; gesperrt: boolean }) {
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `payrollCompensation:${art.id}`,
    bezeichnung: `Entlohnungsart ${art.name}`,
    daten: art as unknown as Datensatz,
    schreibe: (patch, stand) =>
      api.patch<Datensatz>(`/payroll/collective-agreements/${art.id}`, patch, mitStand(stand)).then((r) => r.data),
    keepalive: { url: `/payroll/collective-agreements/${art.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>;
  const gruppe = useGruppe(stelle, { label: art.name });
  const stand = useStellenDaten(stelle);
  const pauschal = stand.kind === 'FLAT';
  const mitStunden = pauschal && stand.flatMode === 'OVERTIME_INCLUDED';

  const name = useFeld(stelle, 'name', { gruppe, pflicht: true, label: 'Bezeichnung', gesperrt });
  // Pauschal ohne Modus gibt es nicht: Wer auf „pauschal" stellt, beginnt mit „nur Fehlzeiten".
  const kind = useFeld<string>(stelle, 'kind', {
    gruppe,
    art: 'auswahl',
    label: 'Art',
    gesperrt,
    folge: (wert) => (wert === 'FLAT' ? { flatMode: stand.flatMode ?? 'ABSENCES_ONLY' } : { flatMode: null, includedOvertimeHours: null }),
  });
  const modus = useFeld<string | null>(stelle, 'flatMode', {
    gruppe,
    art: 'auswahl',
    label: 'Pauschal-Modus',
    gesperrt: gesperrt || !pauschal,
    folge: (wert) => (wert === 'OVERTIME_INCLUDED' ? {} : { includedOvertimeHours: null }),
  });
  const stunden = useFeld<number | null>(stelle, 'includedOvertimeHours', {
    gruppe,
    art: 'zahl',
    stellen: 2,
    label: 'Abgegoltene Stunden',
    gesperrt: gesperrt || !mitStunden,
    pruefe: (w) => (w !== null && (w < 0 || w > 200) ? 'Zwischen 0 und 200 Stunden' : null),
  });
  // Teil 6: Die Vorlage ist der Standard unter den Einstellungen dieser Entlohnungsart; pauschal ohne Stunden rechnet nichts davon.
  const vorlage = useFeld<string | null>(stelle, 'templateKey', { gruppe, art: 'auswahl', label: 'KV-Vorlage', gesperrt });
  const aktiv = useFeld<boolean>(stelle, 'isActive', { gruppe, art: 'schalter', label: 'Aktiv', gesperrt });

  return (
    <ZeilenBlock gruppe={gruppe} schluessel={art.id}>
      <Spalte spalte="name">
        <input aria-label="Bezeichnung" maxLength={120} className={`${zelle} font-medium`} data-start {...name.props} />
      </Spalte>
      <Spalte spalte="kind">
        <select aria-label="Art" className={zelle} {...kind.props}>
          <option value="COLLECTIVE">Kollektivvertrag</option>
          <option value="FLAT">pauschal</option>
        </select>
      </Spalte>
      <Spalte spalte="flatMode">
        {pauschal ? (
          <select aria-label="Pauschal-Modus" className={zelle} {...modus.props}>
            <option value="ABSENCES_ONLY">nur Fehlzeiten</option>
            <option value="TRACK_ONLY">zur Kontrolle</option>
            <option value="OVERTIME_INCLUDED">Überstundenpauschale</option>
          </select>
        ) : (
          <span className="block py-1 text-sm text-muted-foreground">nach Stunden</span>
        )}
      </Spalte>
      <Spalte spalte="includedOvertimeHours">
        {mitStunden ? (
          <input aria-label="Abgegoltene Stunden je Monat" inputMode="decimal" className={`${zelle} text-right tabular-nums`} {...stunden.props} />
        ) : (
          <span />
        )}
      </Spalte>
      <Spalte spalte="templateKey">
        <select aria-label="KV-Vorlage" className={zelle} {...vorlage.props}>
          <option value="">wie der Betrieb</option>
          {KV_VORLAGEN.map((v) => (
            <option key={v.schluessel} value={v.schluessel} title={v.name}>
              {v.kurz}
            </option>
          ))}
        </select>
      </Spalte>
      <Spalte spalte="employees">
        <span className="block py-1 text-right text-sm tabular-nums text-muted-foreground">{art.employees ?? 0}</span>
      </Spalte>
      <Spalte spalte="isActive">
        <span className="flex items-center justify-center gap-1">
          <input type="checkbox" aria-label="Aktiv" className="h-4 w-4 accent-[hsl(var(--primary))]" {...aktiv.schalter} />
          <FeldStatusZeichen status={aktiv} />
        </span>
      </Spalte>
    </ZeilenBlock>
  );
}

/** Die leere letzte Zeile: legt beim Verlassen an, sobald eine Bezeichnung dasteht. */
function NeueArt({ onAngelegt, onFehler }: { onAngelegt: () => void; onFehler: (t: string | null) => void }) {
  const leer = { name: '', kind: 'COLLECTIVE' as 'COLLECTIVE' | 'FLAT' };
  const [neu, setNeu] = useState(leer);

  const anlegen = async () => {
    if (neu.name.trim().length < 2) return;
    try {
      onFehler(null);
      await createCollectiveAgreement({ name: neu.name.trim(), kind: neu.kind });
      setNeu(leer);
      onAngelegt();
    } catch (err) {
      const code = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      onFehler(
        code === 'payroll.collectiveAgreements.duplicateName'
          ? 'Eine Entlohnungsart mit dieser Bezeichnung gibt es schon.'
          : apiErrorText(err, 'Die Entlohnungsart konnte nicht angelegt werden.'),
      );
    }
  };

  return (
    <ZeilenBlock neu onVerlassen={() => void anlegen()} onVerwerfen={() => setNeu(leer)}>
      <Spalte spalte="name">
        <input
          aria-label="Bezeichnung der neuen Entlohnungsart"
          className={zelle}
          placeholder="Neue Entlohnungsart"
          maxLength={120}
          value={neu.name}
          onChange={(e) => setNeu((n) => ({ ...n, name: e.target.value }))}
        />
      </Spalte>
      <Spalte spalte="kind">
        <select
          aria-label="Art der neuen Entlohnungsart"
          className={zelle}
          value={neu.kind}
          onChange={(e) => setNeu((n) => ({ ...n, kind: e.target.value as 'COLLECTIVE' | 'FLAT' }))}
        >
          <option value="COLLECTIVE">Kollektivvertrag</option>
          <option value="FLAT">pauschal</option>
        </select>
      </Spalte>
      <Spalte spalte="flatMode" leer>
        <span />
      </Spalte>
      <Spalte spalte="includedOvertimeHours" leer>
        <span />
      </Spalte>
      <Spalte spalte="templateKey" leer>
        <span />
      </Spalte>
      <Spalte spalte="employees" leer>
        <span />
      </Spalte>
      <Spalte spalte="isActive" leer>
        <span />
      </Spalte>
    </ZeilenBlock>
  );
}
